import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/api/queryKeys';
import {
  HUB_METHODS_NAMES,
  useHubSiteGroup,
  useWebSockets,
} from '@/hooks/useWebSockets';
import {
  CommandCentreOperator,
  CommandCentrePosition,
  OperatorStatus,
} from '@/types';

import {
  coercePositionPayload,
  coercePresencePayload,
  coerceStatePayload,
  upsertPosition,
} from '../utils/commandCentreSocketPayload';

export const useCommandCentreSocket = (siteId?: number) => {
  const queryClient = useQueryClient();
  const enabled = !!siteId;

  useHubSiteGroup(siteId);

  const invalidateEvents = useCallback(() => {
    if (!siteId) return;
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.commandCentreEvents, siteId],
    });
  }, [queryClient, siteId]);

  const invalidateOperators = useCallback(() => {
    if (!siteId) return;
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.commandCentreOperators, siteId],
    });
  }, [queryClient, siteId]);

  const patchPositions = useCallback(
    (
      updater: (current: CommandCentrePosition[]) => CommandCentrePosition[],
    ) => {
      if (!siteId) return;
      queryClient.setQueryData<CommandCentrePosition[]>(
        [QUERY_KEYS.commandCentrePositions, siteId],
        (current = []) => updater(current),
      );
    },
    [queryClient, siteId],
  );

  const refreshOperatorIfMissing = useCallback(
    (operatorId: number) => {
      if (!siteId) return;
      const current = queryClient.getQueryData<CommandCentreOperator[]>([
        QUERY_KEYS.commandCentreOperators,
        siteId,
      ]);
      if (current?.some((op) => op.userId === operatorId)) return;
      invalidateOperators();
    },
    [invalidateOperators, queryClient, siteId],
  );

  const patchOperatorCoords = useCallback(
    (operatorId: number, lat?: number, lng?: number) => {
      if (!siteId || lat == null || lng == null) return;
      queryClient.setQueryData<CommandCentreOperator[]>(
        [QUERY_KEYS.commandCentreOperators, siteId],
        (current) => {
          if (!current) return current;
          return current.map((op) =>
            op.userId === operatorId
              ? {
                  ...op,
                  latitude: lat,
                  longitude: lng,
                  lastSeenAt: new Date().toISOString(),
                  currentStatus: 'Live' as OperatorStatus,
                }
              : op,
          );
        },
      );
    },
    [queryClient, siteId],
  );

  const handlePositionUpdated = useCallback(
    (raw: Record<string, unknown>) => {
      const payload = coercePositionPayload(raw);
      if (!siteId || payload.siteId !== siteId) return;
      patchPositions((current) => upsertPosition(current, payload));
      patchOperatorCoords(payload.operatorId, payload.lat, payload.lng);
      refreshOperatorIfMissing(payload.operatorId);
    },
    [patchOperatorCoords, patchPositions, refreshOperatorIfMissing, siteId],
  );

  const handleStateChanged = useCallback(
    (raw: Record<string, unknown>) => {
      const payload = coerceStatePayload(raw);
      if (!siteId || payload.siteId !== siteId) return;
      patchPositions((current) => upsertPosition(current, payload));
      refreshOperatorIfMissing(payload.operatorId);
    },
    [patchPositions, refreshOperatorIfMissing, siteId],
  );

  const handlePresenceUpdated = useCallback(
    (raw: Record<string, unknown>) => {
      const payload = coercePresencePayload(raw);
      if (!siteId || payload.locationId !== siteId) return;
      // Do not patch positions here: poll also emits PositionUpdated/StateChanged with
      // accurate ageSeconds/linkHealth. Resetting them to 0/'Good' caused weak-link flicker.
      invalidateOperators();
    },
    [invalidateOperators, siteId],
  );

  useWebSockets(
    HUB_METHODS_NAMES.CommandCentreEventCreated,
    invalidateEvents,
    enabled,
  );
  useWebSockets(
    HUB_METHODS_NAMES.OperatorMetricsUpdated,
    invalidateOperators,
    enabled,
  );
  useWebSockets(
    HUB_METHODS_NAMES.OperatorPresenceUpdated,
    handlePresenceUpdated,
    enabled,
  );
  useWebSockets(
    HUB_METHODS_NAMES.PositionUpdated,
    handlePositionUpdated,
    enabled,
  );
  useWebSockets(HUB_METHODS_NAMES.StateChanged, handleStateChanged, enabled);
};
